import React, { useState, useEffect } from 'react';

import { useGetRestingHeartRate } from 'api/services';
import ChartModal from 'components/ChartModal/ChartModal';
import { chartsKey } from 'utils/constants';

import { getChartBgColors } from './utils/chartColorizing';
import {
	handleChartData,
	gradientBgPlugin,
	adaptTwoColumnsTableData,
	renameObjectsProp,
	getChartMaxValue,
	getChartMinValue,
	defaultChartAxisValues,
	increaseOrDecreaseMaxYThreshold,
} from './utils/chartHelpers';
import {
	defaultConfig,
	tooltipCallbackBPM,
	ticksCallbackBPM,
	styledChartElements,
} from './utils/customViewChart';

const RestingHeartRate = ({ closeChart, isOpen }) => {
	const [data, setData] = useState({});
	const [tableData, setTableData] = useState(null);
	const [chartConfig, setChartConfig] = useState(null);
	const [labelsData, setLabelsData] = useState(undefined);

	const { data: restingHeartData, loading } = useGetRestingHeartRate();
	const isDataAvailable =
		restingHeartData?.data && restingHeartData.data.length > 0;
	const lastElement = isDataAvailable && restingHeartData.data.length - 1;

	useEffect(() => {
		if (isDataAvailable) {
			const refValues = getRefValues(restingHeartData?.referential_values);
			const dataAdapted = handleChartData(restingHeartData.data);
			const lastResultRestingHeartRate = restingHeartData.data[lastElement];
			const tableDataAdapted = adaptTwoColumnsTableData(
				restingHeartData?.referential_values,
				lastResultRestingHeartRate,
				'Score',
				'Rating'
			);

			setData(dataAdapted);
			setTableData(tableDataAdapted);
			setLabelsData(dataAdapted?.labels);
			handleChartConfig(refValues);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [restingHeartData]);

	const getRefValues = (refValues) =>
		(refValues &&
			refValues.length > 0 &&
			renameObjectsProp(refValues, 'score', 'rating')) ||
		null;

	const handleChartConfig = (refValues) => {
		let minY = defaultChartAxisValues[chartsKey.restingHeartRate].minY;
		let maxY = defaultChartAxisValues[chartsKey.restingHeartRate].maxY;

		const minValueOfY = getChartMinValue(restingHeartData.data, 'value');
		const maxValueOfY = getChartMaxValue(restingHeartData.data, 'value');

		if (maxValueOfY && maxValueOfY >= maxY) {
			maxY = maxValueOfY + increaseOrDecreaseMaxYThreshold.large;
		}

		if (minValueOfY && minValueOfY <= minY) {
			minY = minValueOfY - increaseOrDecreaseMaxYThreshold.large;
		}
		const chartColors =
			(refValues && getChartBgColors(refValues, maxY, minY)) || [];

		const config = {
			...defaultConfig,
			plugins: {
				...defaultConfig.plugins,
				backgrounds: {
					hbars: chartColors,
				},
				tooltip: {
					...defaultConfig.plugins.tooltip,
					callbacks: { label: tooltipCallbackBPM },
				},
			},
			elements: styledChartElements(lastElement),
			scales: {
				...defaultConfig.scales,
				y: {
					...defaultConfig.scales.y,
					min: minY,
					max: maxY,
					ticks: {
						...defaultConfig.scales.y.ticks,
						callback: ticksCallbackBPM,
						stepSize: 5,
						maxTicksLimit: 20,
					},
				},
			},
		};

		setChartConfig(config);
	};

	return (
		<ChartModal
			data={data}
			loading={loading}
			tableData={tableData}
			labelsData={labelsData}
			closeChart={closeChart}
			chartConfig={chartConfig}
			chartPlugins={gradientBgPlugin}
			isOpen={isOpen}
		/>
	);
};

export default RestingHeartRate;
